import React, { useEffect, useCallback } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Reusable image lightbox with prev/next navigation, keyboard support and a
 * position counter. Used across AI Avatar / AI Talent / Ambassador profiles.
 *
 * Props:
 *  - images: array of string URLs OR objects { url, caption }
 *  - index:  number | null  (null/undefined = closed)
 *  - onIndexChange(newIndex)
 *  - onClose()
 */
export const ImageLightbox = ({ images = [], index, onIndexChange, onClose }) => {
  const list = (images || []).map((im) => (typeof im === 'string' ? { url: im } : im)).filter((im) => im && im.url);
  const open = index !== null && index !== undefined && index >= 0 && index < list.length;
  const showNav = list.length > 1;

  const goPrev = useCallback(() => onIndexChange((index - 1 + list.length) % list.length), [index, list.length, onIndexChange]);
  const goNext = useCallback(() => onIndexChange((index + 1) % list.length), [index, list.length, onIndexChange]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft' && showNav) goPrev();
      else if (e.key === 'ArrowRight' && showNav) goNext();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, showNav, goPrev, goNext, onClose]);

  if (!open) return null;
  const current = list[index];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-black/95 border-0 p-2" data-testid="image-lightbox">
        <div className="relative">
          <img src={current.url} alt={current.caption || 'Preview'}
               className="w-full max-h-[80vh] object-contain rounded-lg" data-testid="image-lightbox-img" />
          {current.caption && (
            <p className="absolute top-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full max-w-[80%] truncate">
              {current.caption}
            </p>
          )}
          {showNav && (
            <>
              <button onClick={goPrev} data-testid="lightbox-prev"
                      aria-label="Previous image"
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button onClick={goNext} data-testid="lightbox-next"
                      aria-label="Next image"
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors">
                <ChevronRight className="w-6 h-6" />
              </button>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full" data-testid="lightbox-counter">
                {index + 1} / {list.length}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageLightbox;
